import { t, type I18nLocale } from '@ardoq/i18n';
import {
  type ComponentOverviewLabels,
  type SidebarContentLabels,
  DEFAULT_CHANGE_REQUEST_TOOLTIP,
} from '@ardoq/component-overview';
import { ViewIds } from '@ardoq/api-types';
import { type LockedFieldReason } from './types';

export const getLockedFieldPopoverText = (
  lockedReason: LockedFieldReason,
  i18nLocale: I18nLocale
): string => {
  if (lockedReason === 'calculated') {
    return t(
      i18nLocale,
      'componentOverview:fields.calculatedFieldLocked',
      "This is a calculated field and can't be edited here"
    );
  }
  return t(
    i18nLocale,
    'componentOverview:fields.defaultFieldLocked',
    "This is a default field and can't be edited here"
  );
};

const getParentChildReferenceName = (i18nLocale: I18nLocale): string =>
  t(
    i18nLocale,
    'componentOverview:package.references.parentChildName',
    'Is parent of'
  );

/**
 * Builds the optional translation labels passed into the
 * @ardoq/component-overview package. The package itself stays
 * translation-free; this consumer wires up the German translations
 * registered under the `componentOverview` namespace.
 */
export const toComponentOverviewLabels = (
  i18nLocale: I18nLocale
): ComponentOverviewLabels => ({
  componentNotFoundTitle: t(
    i18nLocale,
    'componentOverview:package.componentNotFound.title',
    'Component not found'
  ),
  componentNotFoundMessage: t(
    i18nLocale,
    'componentOverview:package.componentNotFound.message',
    'Something went wrong. Please try again later or get in touch with customer support.'
  ),
  descriptionTitle: t(
    i18nLocale,
    'componentOverview:package.description',
    'Description'
  ),
  priorityFieldsTitle: t(
    i18nLocale,
    'componentOverview:package.priorityFields',
    'Priority fields'
  ),
  customFieldsTitle: t(
    i18nLocale,
    'componentOverview:package.customFields',
    'Custom fields'
  ),
  defaultFieldsTitle: t(
    i18nLocale,
    'componentOverview:package.defaultFields',
    'Default fields'
  ),
  changePriorityFieldsLabel: t(
    i18nLocale,
    'componentOverview:package.changePriorityFields',
    'Change priority fields'
  ),
  emptyBusinessCriticalFieldsTitle: t(
    i18nLocale,
    'componentOverview:package.emptyPriorityFields.title',
    'No priority fields selected'
  ),
  emptyBusinessCriticalFieldsDescription: (componentTypeName: string) =>
    t(
      i18nLocale,
      'componentOverview:package.emptyPriorityFields.description',
      { componentTypeName },
      `Make it easier for everyone to find important information. Select the priority {{componentTypeName}} fields to highlight in this section`
    ),
  selectPriorityFieldsLabel: t(
    i18nLocale,
    'componentOverview:package.selectPriorityFields',
    'Select priority fields'
  ),
  detailsEmptyStateTitle: t(
    i18nLocale,
    'componentOverview:package.detailsEmpty.title',
    'No Details'
  ),
  submitUpdateLabel: t(
    i18nLocale,
    'componentOverview:package.submitUpdate',
    'Submit an update'
  ),
  changeRequestTooltip: t(
    i18nLocale,
    'componentOverview:package.changeRequestTooltip',
    DEFAULT_CHANGE_REQUEST_TOOLTIP
  ),
  viewpoints: {
    subtitle: t(
      i18nLocale,
      'componentOverview:package.viewpoints.subtitle',
      'This helps you visualize data, each Viewpoint is typically created to answer a question you might have'
    ),
    noViewpointsTitle: t(
      i18nLocale,
      'componentOverview:package.viewpoints.empty.title',
      'No viewpoints available'
    ),
    noViewpointsForSearch: (searchPhrase: string) =>
      t(
        i18nLocale,
        'componentOverview:package.viewpoints.empty.search',
        { searchPhrase },
        `There is no viewpoint with a title matching "{{searchPhrase}}".`
      ),
    noViewpointsForType: (componentType: string) =>
      t(
        i18nLocale,
        'componentOverview:package.viewpoints.empty.type',
        { componentType },
        `There are no saved viewpoints for the {{componentType}} component type.`
      ),
    carouselLabels: {
      unpublishedTag: t(
        i18nLocale,
        'componentOverview:package.viewpoints.unpublishedTag',
        'Unpublished'
      ),
      favoriteTag: t(
        i18nLocale,
        'componentOverview:package.viewpoints.favoriteTag',
        'Favorite'
      ),
      viewStyleTitles: {
        [ViewIds.SIMPLE_BLOCK_DIAGRAM]: t(
          i18nLocale,
          'componentOverview:package.viewpoints.simpleBlockDiagram',
          'Simple Block Diagram'
        ),
        [ViewIds.BLOCK_DIAGRAM]: t(
          i18nLocale,
          'componentOverview:package.viewpoints.blockDiagram',
          'Block Diagram'
        ),
        [ViewIds.MODERNIZED_BLOCK_DIAGRAM]: t(
          i18nLocale,
          'componentOverview:package.viewpoints.blockDiagram',
          'Block Diagram'
        ),
        [ViewIds.BLOCKS]: t(
          i18nLocale,
          'componentOverview:package.viewpoints.interactiveBlocks',
          'Interactive Blocks'
        ),
        [ViewIds.TIMELINE]: t(
          i18nLocale,
          'componentOverview:package.viewpoints.timeline',
          'Timeline'
        ),
        [ViewIds.RELATIONSHIPS]: t(
          i18nLocale,
          'componentOverview:package.viewpoints.network',
          'Network'
        ),
        [ViewIds.RELATIONSHIPS_3]: t(
          i18nLocale,
          'componentOverview:package.viewpoints.network',
          'Network'
        ),
        [ViewIds.DEPENDENCY_MAP_2]: t(
          i18nLocale,
          'componentOverview:package.viewpoints.dependencyMap',
          'Dependency Map'
        ),
        [ViewIds.BUBBLE]: t(
          i18nLocale,
          'componentOverview:package.viewpoints.bubbleChart',
          'Bubble Chart'
        ),
      },
    },
  },
  references: {
    subtitle: t(
      i18nLocale,
      'componentOverview:package.references.subtitle',
      'See the connections this data has to other parts of the organization'
    ),
    emptyStateTitle: t(
      i18nLocale,
      'componentOverview:package.references.empty.title',
      'Nothing to show'
    ),
    emptySearchContentType: t(
      i18nLocale,
      'componentOverview:package.references.emptySearch.contentType',
      'references'
    ),
    emptySearchTitle: t(
      i18nLocale,
      'componentOverview:package.emptySearch.title',
      'No results found'
    ),
    columnLabels: {
      source: t(
        i18nLocale,
        'componentOverview:package.references.column.source',
        'Source'
      ),
      type: t(
        i18nLocale,
        'componentOverview:package.references.column.type',
        'Type'
      ),
      target: t(
        i18nLocale,
        'componentOverview:package.references.column.target',
        'Target'
      ),
    },
    filterLabels: {
      filterButton: t(
        i18nLocale,
        'componentOverview:package.references.filter.button',
        'Filter'
      ),
      directionHeader: t(
        i18nLocale,
        'componentOverview:package.references.filter.direction',
        'Direction'
      ),
      directionAll: t(
        i18nLocale,
        'componentOverview:package.references.filter.directionAll',
        'All'
      ),
      directionOutgoing: t(
        i18nLocale,
        'componentOverview:package.references.filter.directionOutgoing',
        'Outgoing'
      ),
      directionIncoming: t(
        i18nLocale,
        'componentOverview:package.references.filter.directionIncoming',
        'Incoming'
      ),
      typeHeader: t(
        i18nLocale,
        'componentOverview:package.references.filter.typeHeader',
        'Type'
      ),
      parentChildReferenceName: getParentChildReferenceName(i18nLocale),
    },
    parentChildReferenceName: getParentChildReferenceName(i18nLocale),
  },
  externalDocuments: toExternalDocumentsLabels(i18nLocale),
});

const toExternalDocumentsLabels = (
  i18nLocale: I18nLocale
): NonNullable<ComponentOverviewLabels['externalDocuments']> => ({
  title: count =>
    t(
      i18nLocale,
      'componentOverview:package.externalDocuments.title',
      { count },
      `Linked Documents (${count})`
    ),
  emptyTitle: t(
    i18nLocale,
    'componentOverview:package.externalDocuments.empty.title',
    'No results'
  ),
  emptyMessage: t(
    i18nLocale,
    'componentOverview:package.externalDocuments.empty.message',
    'No documents match your criteria.'
  ),
  columnLabels: {
    type: t(
      i18nLocale,
      'componentOverview:package.externalDocuments.column.type',
      'Type'
    ),
    name: t(
      i18nLocale,
      'componentOverview:package.externalDocuments.column.name',
      'Name'
    ),
    createdBy: t(
      i18nLocale,
      'componentOverview:package.externalDocuments.column.createdBy',
      'Created by'
    ),
  },
});

/**
 * Translation labels for the SidebarContent component (used to render
 * details and references drawers from componentOverview).
 */
export const toSidebarContentLabels = (
  i18nLocale: I18nLocale
): SidebarContentLabels => ({
  detailsDrawer: {
    notFoundContentType: t(
      i18nLocale,
      'componentOverview:package.contentNotFound.details',
      'details'
    ),
    notFoundTitle: t(
      i18nLocale,
      'componentOverview:package.componentNotFound.title',
      'Component not found'
    ),
    descriptionTitle: t(
      i18nLocale,
      'componentOverview:package.description',
      'Description'
    ),
    priorityFieldsTitle: t(
      i18nLocale,
      'componentOverview:package.priorityFields',
      'Priority fields'
    ),
    customFieldsTitle: t(
      i18nLocale,
      'componentOverview:package.customFields',
      'Custom fields'
    ),
    defaultFieldsTitle: t(
      i18nLocale,
      'componentOverview:package.defaultFields',
      'Default fields'
    ),
    changePriorityFieldsLabel: t(
      i18nLocale,
      'componentOverview:package.changePriorityFields',
      'Change priority fields'
    ),
    submitUpdateLabel: t(
      i18nLocale,
      'componentOverview:package.submitUpdate',
      'Submit an update'
    ),
    changeRequestTooltip: t(
      i18nLocale,
      'componentOverview:package.changeRequestTooltip',
      DEFAULT_CHANGE_REQUEST_TOOLTIP
    ),
    externalDocumentsLabels: toExternalDocumentsLabels(i18nLocale),
  },
  referencesDrawer: {
    title: count =>
      t(
        i18nLocale,
        'componentOverview:package.detailsDrawer.referencesTitle',
        { count },
        `References (${count})`
      ),
    notFoundContentType: t(
      i18nLocale,
      'componentOverview:package.contentNotFound.references',
      'references'
    ),
    notFoundTitle: t(
      i18nLocale,
      'componentOverview:package.componentNotFound.title',
      'Component not found'
    ),
    emptyStateTitle: t(
      i18nLocale,
      'componentOverview:package.references.empty.title',
      'Nothing to show'
    ),
    emptySearchContentType: t(
      i18nLocale,
      'componentOverview:package.references.emptySearch.contentType',
      'references'
    ),
    emptySearchTitle: t(
      i18nLocale,
      'componentOverview:package.emptySearch.title',
      'No results found'
    ),
    columnLabels: {
      source: t(
        i18nLocale,
        'componentOverview:package.references.column.source',
        'Source'
      ),
      type: t(
        i18nLocale,
        'componentOverview:package.references.column.type',
        'Type'
      ),
      target: t(
        i18nLocale,
        'componentOverview:package.references.column.target',
        'Target'
      ),
    },
    filterLabels: {
      filterButton: t(
        i18nLocale,
        'componentOverview:package.references.filter.button',
        'Filter'
      ),
      directionHeader: t(
        i18nLocale,
        'componentOverview:package.references.filter.direction',
        'Direction'
      ),
      directionAll: t(
        i18nLocale,
        'componentOverview:package.references.filter.directionAll',
        'All'
      ),
      directionOutgoing: t(
        i18nLocale,
        'componentOverview:package.references.filter.directionOutgoing',
        'Outgoing'
      ),
      directionIncoming: t(
        i18nLocale,
        'componentOverview:package.references.filter.directionIncoming',
        'Incoming'
      ),
      typeHeader: t(
        i18nLocale,
        'componentOverview:package.references.filter.typeHeader',
        'Type'
      ),
      parentChildReferenceName: getParentChildReferenceName(i18nLocale),
    },
    parentChildReferenceName: getParentChildReferenceName(i18nLocale),
  },
});
